import {Injectable, NgZone, OnDestroy} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import {Platform} from '@angular/cdk/platform';
import {Subject} from 'rxjs/Subject';
import {fromEventPattern} from 'rxjs/observable/fromEventPattern';
import {map, takeUntil, startWith, combineLatest} from 'rxjs/operators';
import {from} from 'rxjs/observable/from';
import {forkJoin} from 'rxjs/observable/forkJoin';

export const Breakpoints = {
  XSmall: '(max-width: 599px)',
  Small: '(min-width: 600px) and (max-width: 959px)',
  Medium: '(min-width: 960px) and (max-width: 1279px)',
  Large: '(min-width: 1280px) and (max-width: 1919px)',
  XLarge: '(min-width: 1920px)',

  Handset: '(max-width: 599px) and (orientation: portrait), ' +
    '(max-width: 959px) and (orientation: landscape)',
  Tablet: '(min-width: 600px) and (max-width: 839px) and (orientation: portrait), ' +
    '(min-width: 960px) and (max-width: 1279px) and (orientation: landscape)',
  Web: '(min-width: 840px) and (orientation: portrait), ' +
    '(min-width: 1280px) and (orientation: landscape)',

  HandsetPortrait: '(max-width: 599px) and (orientation: portrait)',
  TabletPortrait: '(min-width: 600px) and (max-width: 839px) and (orientation: portrait)',
  WebPortrait: '(min-width: 840px) and (orientation: portrait)',

  HandsetLandscape: '(max-width: 959px) and (orientation: landscape)',
  TabletLandscape: '(min-width: 960px) and (max-width: 1279px) and (orientation: landscape)',
  WebLandscape: '(min-width: 1280px) and (orientation: landscape)',
  customDesktop: '(min-width: 960px)',
};

/** The current state of a layout breakpoint. */
export interface BreakpointState {
  /** Whether the breakpoint is currently matching. */
  matches: boolean;
}

interface Query {
  observable: Observable<BreakpointState>;
  mql: MediaQueryList;
}

/**
 * Global registry for all dynamically-created, injected style tags.
 */
const styleElementForWebkitCompatibility: Map<string, HTMLStyleElement> = new Map();

/**
 * Traido desde el CDK, no es lo mas lindo del mundo pero funciona, en A13 se usa directamente desde el cdk, como debe ser
 * */
@Injectable()
export class BreakpointsObserverService implements OnDestroy {
  private _matchMedia: (query: string) => MediaQueryList;

  /**  A map of all media queries currently being listened for. */
  private _queries: Map<string, Query> = new Map();
  /** A subject for all other observables to takeUntil based on. */
  private _destroySubject: Subject<{}> = new Subject();

  constructor(
    private platform: Platform,
    private zone: NgZone
  ) {
    this._matchMedia = this.platform.isBrowser && window.matchMedia ?
      // matchMedia is bound to the window scope intentionally as it is an illegal invocation to
      // call it from a different scope.
      window.matchMedia.bind(window) :
      noopMatchMedia;
  }

  /** Completes the active subject, signalling to all other observables to complete. */
  ngOnDestroy() {
    this._destroySubject.next();
    this._destroySubject.complete();
  }

  /**
   * Evaluates the given media query and returns the native MediaQueryList from which results
   * can be retrieved.
   * Confirms the layout engine will trigger for the selector query provided and returns the
   * MediaQueryList for the query provided.
   */
  matchMedia(query: string): MediaQueryList {
    if (this.platform.WEBKIT) {
      createEmptyStyleRule(query);
    }
    return this._matchMedia(query);
  }

  /**
   * Gets an observable of results for the given queries that will emit new results for any changes
   * in matching of the given queries.
   * @returns A stream of matches for the given queries.
   */

  observe(value: string[]): Observable<BreakpointState> {
    let observables = value.map(query => this._registerQuery(query).observable);
    return Observable.combineLatest(observables, (...results: any[]) => {
      const matches = results.some(result => result.matches);
      return { matches };
    });
  }

  observeDesktop(): Observable<BreakpointState> {
    return this._registerQuery(Breakpoints.customDesktop).observable
  }

  /** Registers a specific query to be listened for. */
  private _registerQuery(query: string): Query {
    // Only set up a new MediaQueryList if it is not already being listened for.
    if (this._queries.has(query)) {
      return this._queries.get(query)!;
    }

    let mql: MediaQueryList = this.matchMedia(query);
    // Create callback for match changes and add it is as a listener.
    let queryObservable = fromEventPattern(
      // Listener callback methods are wrapped to be placed back in ngZone. Callbacks must be placed
      // back into the zone because matchMedia is only included in Zone.js by loading the
      // webapis-media-query.js file alongside the zone.js file.  Additionally, some browsers do not
      // have MediaQueryList inherit from EventTarget, which causes inconsistencies in how Zone.js
      // patches it.
      (listener: MediaQueryListListener) => {
        mql.addListener((e: MediaQueryList) => this.zone.run(() => listener(e)));
      },
      (listener: MediaQueryListListener) => {
        mql.removeListener((e: MediaQueryList) => this.zone.run(() => listener(e)));
      })
      .takeUntil(this._destroySubject)
      .startWith(mql)
      .map((nextMql: MediaQueryList) => ({matches: nextMql.matches}))

    // Add the MediaQueryList to the set of queries.
    let output = {observable: queryObservable, mql: mql};
    this._queries.set(query, output);
    return output;
  }

}
/**
 * For Webkit engines that only trigger the MediaQueryListListener when there is at least one CSS
 * selector for the respective media query.
 */
function createEmptyStyleRule(query: string) {
  if (!styleElementForWebkitCompatibility.has(query)) {
    try {
      const style = document.createElement('style');

      style.setAttribute('type', 'text/css');
      if (!style.sheet) {
        const cssText = `@media ${query} {.fx-query-test{ }}`;
        style.appendChild(document.createTextNode(cssText));
      }

      document.getElementsByTagName('head')[0].appendChild(style);

      // Store in private global registry
      styleElementForWebkitCompatibility.set(query, style);
    } catch (e) {
      console.error(e);
    }
  }
}

/** No-op matchMedia replacement for non-browser platforms. */
function noopMatchMedia(query: string): MediaQueryList {
  return {
    matches: query === 'all' || query === '',
    media: query,
    addListener: () => {},
    removeListener: () => {}
  };
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/services/breakpoints-observer.service.ts